/**
 * LiveKit transport for every scope that broadcasts: `church`, `fwtv`, `event`.
 *
 * The broadcaster publishes one copy into the SFU and the server fans it out,
 * so the ceiling is the server's rather than one phone's uplink, and LiveKit's
 * own ICE/TURN handles the networks a direct peer connection cannot cross.
 *
 * No key ever reaches this file: the backend mints a short-lived participant
 * token after it has checked who is asking, scope by scope.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Room, RoomEvent, Track, LocalAudioTrack, LocalVideoTrack, ConnectionState,
} from 'livekit-client';
import { describeCameraError, requestCameraStream } from '@/lib/cameraAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authToken = () =>
  // The app stores the session under `eventflow_token`; `admin_token` only
  // exists while a super admin is impersonating, and must win when it does.
  localStorage.getItem('admin_token') || localStorage.getItem('eventflow_token')
  || localStorage.getItem('token');

/** Which transport the server wants us to use, asked once. */
export function useLiveTransport() {
  const [state, setState] = useState({ loading: true, transport: 'mesh', livekit: false });
  useEffect(() => {
    let dead = false;
    fetch(`${API_URL}/api/live/capabilities`)
      .then((r) => r.json())
      .then((b) => !dead && setState({ loading: false, ...b }))
      .catch(() => !dead && setState({ loading: false, transport: 'mesh', livekit: false }));
    return () => { dead = true; };
  }, []);
  return state;
}

/**
 * The broadcaster. Camera first so the framing can be checked before anyone
 * sees it, then one connection that publishes into the room.
 */
export function useLiveKitPublisher(scope, ident) {
  const [status, setStatus] = useState('idle');     // idle|starting|live|error
  const [error, setError] = useState('');
  const [errorSteps, setErrorSteps] = useState([]);
  const [hasAudio, setHasAudio] = useState(true);
  const [viewers, setViewers] = useState(0);
  const [facing, setFacing] = useState('environment');

  const roomRef = useRef(null);
  const tracksRef = useRef([]);
  const videoElRef = useRef(null);

  const attach = useCallback((el) => {
    videoElRef.current = el;
    const video = tracksRef.current.find((t) => t.kind === Track.Kind.Video);
    if (el && video) video.attach(el);
  }, []);

  const openCamera = useCallback(async (which = facing) => {
    setError('');
    setErrorSteps([]);
    try {
      const existing = tracksRef.current.find((t) => t.kind === Track.Kind.Video);
      if (existing && roomRef.current) {
        // Mid-broadcast flip: restart the same track so viewers keep their
        // subscription rather than renegotiating.
        await existing.restartTrack({ facingMode: { ideal: which } });
        setFacing(which);
        if (videoElRef.current) existing.attach(videoElRef.current);
        return true;
      }
      tracksRef.current.forEach((t) => t.stop());
      // Asked for through the shared helper so a blocked microphone costs the
      // sound rather than the whole broadcast.
      const { stream, hasAudio: gotAudio } = await requestCameraStream(which);
      setHasAudio(gotAudio);
      const tracks = [new LocalVideoTrack(stream.getVideoTracks()[0])];
      if (gotAudio && stream.getAudioTracks()[0]) {
        tracks.push(new LocalAudioTrack(stream.getAudioTracks()[0]));
      }
      tracksRef.current = tracks;
      setFacing(which);
      const video = tracks.find((t) => t.kind === Track.Kind.Video);
      if (video && videoElRef.current) video.attach(videoElRef.current);
      return true;
    } catch (e) {
      const { message, steps } = describeCameraError(e);
      setError(message);
      setErrorSteps(steps);
      setStatus('error');
      return false;
    }
  }, [facing]);

  const goLive = useCallback(async () => {
    setStatus('starting');
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/live/${scope}/${ident}/publisher-token`,
        { method: 'POST', headers: { Authorization: `Bearer ${authToken()}` } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || 'Could not get permission to broadcast');

      const room = new Room({ adaptiveStream: false, dynacast: true });
      room
        .on(RoomEvent.ParticipantConnected, () => setViewers(room.remoteParticipants.size))
        .on(RoomEvent.ParticipantDisconnected, () => setViewers(room.remoteParticipants.size))
        .on(RoomEvent.Disconnected, () => { setStatus('idle'); setViewers(0); });
      await room.connect(body.url, body.token);
      roomRef.current = room;
      for (const track of tracksRef.current) {
        await room.localParticipant.publishTrack(track);
      }
      setViewers(room.remoteParticipants.size);
      setStatus('live');
      return true;
    } catch (e) {
      setError(e.message || 'Could not start the broadcast.');
      setStatus('error');
      return false;
    }
  }, [scope, ident]);

  const stop = useCallback(() => {
    try { roomRef.current?.disconnect(); } catch { /* already gone */ }
    roomRef.current = null;
    tracksRef.current.forEach((t) => t.stop());
    tracksRef.current = [];
    if (videoElRef.current) videoElRef.current.srcObject = null;
    setViewers(0);
    setStatus('idle');
  }, []);

  useEffect(() => () => {
    try { roomRef.current?.disconnect(); } catch { /* unmounting */ }
    tracksRef.current.forEach((t) => t.stop());
  }, []);

  // LiveKit carries its own relay, so there is no separate TURN to warn about.
  return { status, error, errorSteps, hasAudio, viewers, facing, attach,
           openCamera, goLive, stop, hasRelay: true, transport: 'livekit' };
}

/** A seat in the audience: receive-only, by grant as well as by code. */
export function useLiveKitViewer(scope, ident, enabled) {
  const [state, setState] = useState('connecting');   // connecting|waiting|playing|offline|failed
  const [message, setMessage] = useState('');
  const roomRef = useRef(null);
  const elRef = useRef(null);
  const audioElRef = useRef(null);

  const attach = useCallback((el) => { elRef.current = el; }, []);

  useEffect(() => {
    if (!enabled || !ident) return undefined;
    let dead = false;
    let room = null;

    const run = async () => {
      try {
        const token = authToken();
        const res = await fetch(
          `${API_URL}/api/live/public/${scope}/${ident}/viewer-token`,
          { method: 'POST',
            // Sent when there is one: a private event checks the seat against
            // its delegate list, a public page sends nothing.
            headers: token ? { Authorization: `Bearer ${token}` } : {} });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.detail || 'This stream is not running.');
        if (dead) return;

        room = new Room({ adaptiveStream: true });
        roomRef.current = room;
        room
          .on(RoomEvent.TrackSubscribed, (track) => {
            if (track.kind === Track.Kind.Video && elRef.current) {
              track.attach(elRef.current);
              setState('playing');
            }
            if (track.kind === Track.Kind.Audio) {
              audioElRef.current = track.attach();
              audioElRef.current.play?.().catch(() => { /* autoplay policy */ });
            }
          })
          .on(RoomEvent.TrackUnsubscribed, (track) => {
            track.detach();
            if (track.kind === Track.Kind.Video) setState('waiting');
          })
          .on(RoomEvent.Disconnected, () => !dead && setState('offline'))
          .on(RoomEvent.Reconnecting, () => !dead && setState('connecting'));

        await room.connect(body.url, body.token, { autoSubscribe: true });
        if (dead) { room.disconnect(); return; }
        const hasVideo = [...room.remoteParticipants.values()].some((p) =>
          [...p.trackPublications.values()].some((t) => t.kind === Track.Kind.Video));
        setState(hasVideo ? 'playing' : 'waiting');
      } catch (e) {
        if (dead) return;
        setState('failed');
        setMessage(e.message || '');
      }
    };
    run();

    return () => {
      dead = true;
      try { room?.disconnect(); } catch { /* already gone */ }
      audioElRef.current?.remove?.();
      roomRef.current = null;
    };
  }, [scope, ident, enabled]);

  const connected = () => roomRef.current?.state === ConnectionState.Connected;

  return { state, message, attach, connected };
}
